import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Link,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useCorporateMasterEnabled } from "@/hooks/useCorporateMasterEnabled";
import {
  getCategories,
  supportLinks,
  type CategoryData,
  type MenuItemData,
} from "@/configs/sideMenuData";
import { getHrefRel, getHrefTarget } from "@/utils/externalLink";
import { isInternalLink } from "@/utils/link";
import { useHeaderMenu } from "@/features/header/hooks/useHeaderMenu";
import { HEADER_HEIGHT } from "@/features/header";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { useSideMenuState } from "@/hooks/useSideMenuState";
import { useContactFormDialog } from "@/hooks/useContactFormDialog";
import { useIsMonthlyMember } from "@/features/brandBadge";

/** 月額会員限定マーカーの上品なミュートゴールド。 */
const PREMIUM_GOLD = "#9a740c";

export const SIDEMENU_WIDTH = 260;
const COLLAPSED_WIDTH = 72;

export const SideMenu: React.FC = () => {
  const { isVisible, handleClickLogout } = useHeaderMenu();
  const location = useLocation();
  // 相続後リユース候補は月額会員限定。フラグに加え会員判定でメニュー可否を決める。
  const isMonthlyMember = useIsMonthlyMember();
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});
  const { isMobileOpen, closeMobileMenu, isCollapsed, setIsCollapsed } =
    useSideMenuState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { openContactForm } = useContactFormDialog();

  const {
    realEstateRegistrationAggregationOfNames,
    realEstateRegistrationMonitoring,
    commercialBookList,
    commercialCorporationList,
    commercialRegistrationReceptionBook,
    commercialRegistrationAggregationOfNames,
    commercialRegistrationMonitoring,
    commercialPictureAnalysis,
    allMattersAnalysis,
    dmManagement,
    emailManagement,
    customerManagement,
    ownerInfoList,
    mypageTop,
    realEstateTitle,
    tradePriceSearch,
    rightsSectionOtsukuSearch,
    inheritanceReuseCandidates,
    realEstateResearchRequest,
  } = useFeatureFlags();
  const corporateMasterEnabled = useCorporateMasterEnabled();

  const toggleCategory = (category: string): void => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // メニューデータ定義（sideMenuData.tsから取得）
  const categories = useMemo(
    () =>
      getCategories({
        realEstateRegistrationAggregationOfNames:
          !!realEstateRegistrationAggregationOfNames,
        realEstateRegistrationMonitoring: !!realEstateRegistrationMonitoring,
        commercialBookList: !!commercialBookList,
        corporateMaster: corporateMasterEnabled,
        commercialCorporationList: !!commercialCorporationList,
        commercialRegistrationReceptionBook:
          !!commercialRegistrationReceptionBook,
        commercialRegistrationAggregationOfNames:
          !!commercialRegistrationAggregationOfNames,
        commercialRegistrationMonitoring: !!commercialRegistrationMonitoring,
        commercialPictureAnalysis: !!commercialPictureAnalysis,
        allMattersAnalysis: !!allMattersAnalysis,
        dmManagement: !!dmManagement,
        emailManagement: !!emailManagement,
        customerManagement: !!customerManagement,
        ownerInfoList: !!ownerInfoList,
        realEstateTitle: !!realEstateTitle,
        tradePriceSearch: !!tradePriceSearch,
        rightsSectionOtsukuSearch: !!rightsSectionOtsukuSearch,
        // 月額会員限定：フラグ ON かつ会員のときのみ有効化。
        inheritanceReuseCandidates:
          !!inheritanceReuseCandidates && isMonthlyMember,
        realEstateResearchRequest: realEstateResearchRequest === true,
      }),
    [
      realEstateRegistrationAggregationOfNames,
      realEstateRegistrationMonitoring,
      commercialBookList,
      commercialCorporationList,
      commercialRegistrationReceptionBook,
      commercialRegistrationAggregationOfNames,
      commercialRegistrationMonitoring,
      commercialPictureAnalysis,
      allMattersAnalysis,
      dmManagement,
      emailManagement,
      customerManagement,
      ownerInfoList,
      realEstateTitle,
      tradePriceSearch,
      rightsSectionOtsukuSearch,
      inheritanceReuseCandidates,
      realEstateResearchRequest,
      corporateMasterEnabled,
      isMonthlyMember,
    ]
  );

  // メニューグループ（categories, supportLinksをグループ化）
  const menuGroups: CategoryData[][] = useMemo(
    () => [categories, supportLinks],
    [categories]
  );

  // 現在開いているURL（location.pathname）に最も長く一致するアドレスをmenuGroupsから抽出する
  const activeHref = useMemo(() => {
    // 現在URLに完全一致または前方一致する内部リンクか
    const matchesCurrentPath = (href: string | undefined): href is string =>
      !!href &&
      isInternalLink(href) &&
      (location.pathname === href || location.pathname.startsWith(`${href}/`));

    let longest = "";

    // マッチするhrefの中で、より長い（より具体的な）パスがあれば最長値を更新する
    const updateIfLongest = (href: string | undefined): void => {
      if (matchesCurrentPath(href) && href.length > longest.length) {
        longest = href;
      }
    };

    for (const group of menuGroups) {
      for (const category of group) {
        updateIfLongest(category.href);
        category.items?.forEach((item) => {
          updateIfLongest(item.href);
        });
        category.subCategories?.forEach((sub) => {
          sub.items.forEach((item) => {
            updateIfLongest(item.href);
          });
        });
      }
    }

    return longest;
  }, [menuGroups, location.pathname]);

  /**
   * 引数のhrefが、今開いている画面のURL(activeHref)と一致するか調べる
   *
   * @param href - 判定対象のメニューのリンクURL
   * @returns 現在表示中のURLと一致する場合は true
   */
  const isActive = (href: string | undefined): boolean => {
    return !!href && href === activeHref;
  };

  // テキスト部分の共通スタイル
  const textTransitionStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    opacity: isCollapsed ? 0 : 1,
    width: isCollapsed ? 0 : "auto",
    minWidth: isCollapsed ? 0 : "auto",
    m: 0,
    transition: "opacity 0.2s 0.1s, width 0.3s",
  };

  // 共通スタイル
  const iconStyle = {
    minWidth: isCollapsed ? 0 : 28,
    transition: "min-width 0.3s",
  };
  const categoryTextProps = (color: string) =>
    ({
      fontWeight: 500,
      color,
      fontSize: 16,
    } as const);
  const smallIconStyle = { color: "darkgray.main", fontSize: 16 };

  // カテゴリをレンダリングする関数
  const renderCategory = (rawCategory: CategoryData): JSX.Element => {
    // hidden 項目を描画対象から除外し、項目が空になったサブグループごと落とす。
    const category: CategoryData = {
      ...rawCategory,
      items: rawCategory.items?.filter((item) => !item.hidden),
      subCategories: rawCategory.subCategories
        ?.map((sub) => ({
          ...sub,
          items: sub.items.filter((item) => !item.hidden),
        }))
        .filter((sub) => sub.items.length > 0),
    };
    const isExpanded = expandedCategories[category.title];
    const CategoryIcon = category.icon;
    const iconColor = category.isPrimary ? "primary.main" : "darkgray.main";
    const textColor = category.isPrimary ? "black.main" : "darkgray.main";
    const isAccordion = !category.href && !category.actionType;

    const handleClick =
      category.actionType === "contact-dialog"
        ? () => {
            openContactForm();
            if (isMobile) closeMobileMenu();
          }
        : isAccordion
        ? () => {
            if (isCollapsed) {
              setIsCollapsed(false);
              setExpandedCategories((prev) => ({
                ...prev,
                [category.title]: true,
              }));
            } else {
              toggleCategory(category.title);
            }
          }
        : undefined;

    const buttonContent = (
      <ListItemButton
        onClick={handleClick}
        sx={{ p: 2, justifyContent: "flex-start" }}
      >
        <ListItemIcon sx={iconStyle}>
          <CategoryIcon
            sx={{
              color: iconColor,
              fontSize: isCollapsed ? 24 : 16,
              transition: "font-size 0.3s",
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={category.title}
          primaryTypographyProps={categoryTextProps(textColor)}
          sx={textTransitionStyle}
        />
        {isAccordion && (
          <Box sx={textTransitionStyle}>
            {isExpanded ? (
              <ExpandLessIcon sx={smallIconStyle} />
            ) : (
              <ExpandMoreIcon sx={smallIconStyle} />
            )}
          </Box>
        )}
      </ListItemButton>
    );

    // サービスの入口は、展開時はロゴとメニュー行、折りたたみ時はマークだけを出す
    const { serviceSection } = category;
    if (serviceSection && !isCollapsed) {
      const { Section } = serviceSection;
      return (
        <Section
          key={category.title}
          onNavigate={() => {
            if (isMobile) closeMobileMenu();
          }}
        />
      );
    }

    // 折りたたみ時はマークだけなので、サービス名を aria-label と Tooltip で伝える
    if (serviceSection && category.href) {
      return (
        <Box key={category.title}>
          <Tooltip title={serviceSection.name} placement="right">
            <Link
              component={RouterLink}
              to={category.href}
              color="inherit"
              underline="none"
              aria-label={serviceSection.name}
            >
              {buttonContent}
            </Link>
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box key={category.title}>
        {category.href ? (
          isInternalLink(category.href) ? (
            <Link
              component={RouterLink}
              to={category.href}
              color="inherit"
              underline="none"
              onClick={() => {
                if (isMobile) closeMobileMenu();
              }}
            >
              {buttonContent}
            </Link>
          ) : (
            <Link
              href={category.href}
              target={getHrefTarget(category.href)}
              rel={getHrefRel(category.href)}
              color="inherit"
              underline="none"
              onClick={() => {
                if (isMobile) closeMobileMenu();
              }}
            >
              {buttonContent}
            </Link>
          )
        ) : category.actionType === "contact-dialog" ? (
          buttonContent
        ) : (
          <>
            {buttonContent}
            <Collapse
              in={isExpanded}
              timeout="auto"
              unmountOnExit
              sx={{
                "& .MuiCollapse-wrapperInner": {
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.25,
                },
              }}
            >
              {category.subCategories?.map((subCategory, index) => (
                <Box
                  key={subCategory.title || `subcat-${index}`}
                  sx={{
                    backgroundColor: "background.default",
                    px: 1,
                    py: 1,
                    borderRadius: 1,
                  }}
                >
                  {subCategory.title && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        p: 1,
                        color: "black.main",
                        fontWeight: 600,
                        fontSize: { xs: 14, md: 12 },
                      }}
                    >
                      {subCategory.title}
                    </Typography>
                  )}
                  <List
                    dense
                    disablePadding
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    {subCategory.items.map((item) => (
                      <ListItem key={item.label} disablePadding>
                        {renderMenuItem(item)}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </Collapse>
          </>
        )}
      </Box>
    );
  };

  // カテゴリ内にアクティブな要素があるかチェック
  const hasActiveItem = (category: CategoryData): boolean => {
    if (category.subCategories) {
      return category.subCategories.some((sub) =>
        sub.items.some((item) => isActive(item.href))
      );
    }
    if (category.items) {
      return category.items.some((item) => isActive(item.href));
    }
    return false;
  };

  // アクティブな要素があるカテゴリを展開（ページ遷移時にも更新）
  // サイドバーが閉じている場合はカテゴリを展開しない
  useEffect(() => {
    if (isCollapsed) {
      // サイドバーが閉じている場合は全てのカテゴリを閉じる
      setExpandedCategories({});
      return;
    }
    const initialExpanded: Record<string, boolean> = {};
    categories.forEach((category) => {
      initialExpanded[category.title] = hasActiveItem(category);
    });
    setExpandedCategories(initialExpanded);
  }, [categories, location.pathname, isCollapsed]);

  if (!isVisible) return null;

  const renderMenuItem = (item: MenuItemData): JSX.Element => {
    const IconComponent = item.icon;
    const active = isActive(item.href);

    // disabledの場合はgray、アクティブはprimary、それ以外はテキストblack、アイコンdarkgray
    const iconColor = item.disabled
      ? "gray.main"
      : active
      ? "primary.main"
      : "darkgray.main";
    const textColor = item.disabled
      ? "gray.main"
      : active
      ? "primary.main"
      : "black.main";

    const content = (
      <ListItemButton
        sx={{
          py: 1,
          px: 1,
          "&:hover": {
            backgroundColor: item.disabled ? "transparent" : "white.main",
          },
        }}
      >
        {IconComponent && (
          <ListItemIcon sx={{ minWidth: 28 }}>
            <IconComponent
              fontSize="small"
              sx={{ color: iconColor, fontSize: 18 }}
            />
          </ListItemIcon>
        )}
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            variant: "body2",
            color: textColor,
            fontWeight: 400,
            fontSize: { xs: 16, md: 14 },
          }}
          sx={{ m: 0 }}
        />
        {/* 月額会員限定の上品なプレミアムマーカー（末尾に小さなゴールド王冠）。
            サイドバー折りたたみ時は非表示にして崩れを防ぐ。 */}
        {item.premium === true && !isCollapsed && (
          <WorkspacePremiumIcon
            aria-label="月額会員限定"
            sx={{ ml: 0.5, fontSize: 15, color: PREMIUM_GOLD, flexShrink: 0 }}
          />
        )}
      </ListItemButton>
    );

    // disabledの場合はリンクなし
    if (item.disabled) {
      return content;
    }

    // 内部リンクの場合はReact Routerを使用
    if (item.href && isInternalLink(item.href)) {
      return (
        <Link
          component={RouterLink}
          to={item.href}
          color="inherit"
          underline="none"
          sx={{ width: "100%" }}
          onClick={() => {
            if (isMobile) closeMobileMenu();
          }}
        >
          {content}
        </Link>
      );
    }

    // 外部リンクの場合は従来通り
    return (
      <Link
        href={item.href}
        target={getHrefTarget(item.href)}
        rel={getHrefRel(item.href)}
        color="inherit"
        underline="none"
        sx={{ width: "100%" }}
        onClick={() => {
          if (isMobile) closeMobileMenu();
        }}
      >
        {content}
      </Link>
    );
  };

  // PC時のみ表示（スマホ時はモバイル用Drawerを別途表示）
  if (!isMobile) {
    return (
      <Box
        sx={{
          width: isCollapsed ? COLLAPSED_WIDTH : SIDEMENU_WIDTH,
          flexShrink: 0,
        }}
      >
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            "& .MuiDrawer-paper": {
              width: isCollapsed ? COLLAPSED_WIDTH : SIDEMENU_WIDTH,
              boxSizing: "border-box",
              position: "fixed",
              top: HEADER_HEIGHT,
              height: `calc(100vh - ${HEADER_HEIGHT}px)`,
              overflowY: "auto",
              overflowX: "hidden",
              borderRight: "1px solid",
              borderColor: "border.main",
              zIndex: 1,
              transition: "width 0.3s",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {/* mypageTop時のメニュー */}
            {mypageTop &&
              menuGroups.map((group, groupIndex) => (
                <Box
                  key={groupIndex}
                  sx={{
                    p: 1,
                    display: "flex",
                    flexDirection: "column",
                    borderTop: groupIndex > 0 ? "1px solid" : "none",
                    borderColor: "border.main",
                    mb: groupIndex > 0 ? "0" : "auto",
                  }}
                >
                  {group.map(renderCategory)}
                </Box>
              ))}

            {/* 非mypageTop時 */}
            {!mypageTop && (
              <Box>
                {/* アカウント情報 */}
                <Link
                  component={RouterLink}
                  to="/account"
                  color="inherit"
                  underline="none"
                >
                  <ListItemButton sx={{ py: 1, px: 2 }}>
                    <ListItemText
                      primary="アカウント情報"
                      primaryTypographyProps={{
                        variant: "body2",
                        color: "darkgray.main",
                        fontWeight: 400,
                      }}
                    />
                  </ListItemButton>
                </Link>
                <Divider sx={{ my: 1 }} />

                {/* ログアウト */}
                <ListItemButton
                  onClick={handleClickLogout}
                  sx={{ py: 1, px: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <LogoutIcon
                      fontSize="small"
                      sx={{ color: "darkgray.main" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="ログアウト"
                    primaryTypographyProps={{
                      variant: "body2",
                      color: "darkgray.main",
                    }}
                  />
                </ListItemButton>
              </Box>
            )}
          </Box>
        </Drawer>
        {/* VSCode風の境界線上フローティングボタン */}
        {mypageTop && (
          <Box
            component="button"
            onClick={() => {
              if (!isCollapsed) {
                setExpandedCategories({});
              }
              setIsCollapsed(!isCollapsed);
            }}
            sx={{
              position: "fixed",
              left: isCollapsed ? COLLAPSED_WIDTH - 12 : SIDEMENU_WIDTH - 12,
              top: HEADER_HEIGHT + 24,
              width: 24,
              height: 24,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: "border.main",
              backgroundColor: "white.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 2,
              transition: "left 0.3s",
              "&:hover": {
                backgroundColor: "background.default",
              },
            }}
          >
            {isCollapsed ? (
              <ChevronRightIcon sx={{ fontSize: 16, color: "darkgray.main" }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: 16, color: "darkgray.main" }} />
            )}
          </Box>
        )}
      </Box>
    );
  }

  // スマホ時のtemporary Drawer（ヘッダーの下から表示）
  return (
    <>
      <Drawer
        variant="temporary"
        anchor="left"
        open={isMobileOpen}
        onClose={closeMobileMenu}
        transitionDuration={300}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          zIndex: 1000,
          "& .MuiBackdrop-root": {
            top: HEADER_HEIGHT,
          },
          "& .MuiDrawer-paper": {
            width: "100vw",
            boxSizing: "border-box",
            position: "fixed",
            top: HEADER_HEIGHT,
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            overflowY: "auto",
            overflowX: "hidden",
            boxShadow: "none",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* mypageTop時のメニュー */}
          {mypageTop &&
            menuGroups.map((group, groupIndex) => (
              <Box
                key={groupIndex}
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  borderTop: groupIndex > 0 ? "1px solid" : "none",
                  borderColor: "border.main",
                  mb: groupIndex > 0 ? "0" : "auto",
                }}
              >
                {group.map(renderCategory)}
              </Box>
            ))}

          {/* 非mypageTop時 */}
          {!mypageTop && (
            <Box>
              {/* アカウント情報 */}
              <Link
                component={RouterLink}
                to="/account"
                color="inherit"
                underline="none"
                onClick={closeMobileMenu}
              >
                <ListItemButton sx={{ py: 1, px: 2 }}>
                  <ListItemText
                    primary="アカウント情報"
                    primaryTypographyProps={{
                      variant: "body2",
                      color: "darkgray.main",
                      fontWeight: 400,
                    }}
                  />
                </ListItemButton>
              </Link>
              <Divider sx={{ my: 1 }} />

              {/* ログアウト */}
              <ListItemButton onClick={handleClickLogout} sx={{ py: 1, px: 2 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <LogoutIcon
                    fontSize="small"
                    sx={{ color: "darkgray.main" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="ログアウト"
                  primaryTypographyProps={{
                    variant: "body2",
                    color: "darkgray.main",
                  }}
                />
              </ListItemButton>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};
